import { hashRow } from "./mapRows";
import { normalizeVendor } from "./normalizeVendor";
import type { DedupeResult, MappedRow } from "./types";

interface ExistingRecord {
  occurredOn: string;
  amountCents: number;
  vendor: string;
}

/**
 * Partition incoming rows into `duplicates` and `fresh`:
 *   - A row is a duplicate if its hash matches any existing record's hash
 *     (computed with the same date|amount|normalized-vendor recipe).
 *   - A row is also a duplicate if an earlier row in the same batch already
 *     had the same hash — we keep the first occurrence fresh and move
 *     subsequent hits to `duplicates[]`.
 *
 * Pure function — no React, no Supabase.
 */
export async function dedupeRows(
  rows: MappedRow[],
  existing: ExistingRecord[],
): Promise<DedupeResult> {
  // Pre-compute the hash set of already-stored records. We use the same
  // recipe as `mapRows.hashRow` so the lookup is O(1) and case-insensitive
  // via normalizeVendor.
  const existingHashes = new Set<string>();
  for (const rec of existing) {
    const norm = normalizeVendor(rec.vendor);
    const h = await hashRow(rec.occurredOn, rec.amountCents, norm);
    existingHashes.add(h);
  }

  const duplicates: MappedRow[] = [];
  const fresh: MappedRow[] = [];

  // Track in-CSV hashes so the second (and subsequent) occurrence of the
  // same hash within the batch is flagged.
  const seenInBatch = new Set<string>();

  for (const row of rows) {
    if (existingHashes.has(row.hash) || seenInBatch.has(row.hash)) {
      duplicates.push(row);
      continue;
    }
    seenInBatch.add(row.hash);
    fresh.push(row);
  }

  return { duplicates, fresh };
}
